// src/pages/PhotoViewer.tsx

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGesture } from '@use-gesture/react';
import { useAppStore } from '../store/appStore';
import { S3Service } from '../services/s3Service';
import { getNextIndex, getPrevIndex } from '../utils/navigation';
import { PHOTOS_PER_PAGE } from '../models/constants';
import './PhotoViewer.css';

export function PhotoViewer() {
  const { year, event, photoIndex: photoIndexParam } = useParams<{
    year: string;
    event: string;
    photoIndex: string;
  }>();
  const navigate = useNavigate();
  const { awsCredentials } = useAppStore();

  const photoIndex = parseInt(photoIndexParam ?? '0', 10);

  // Photo list state
  const [photoKeys, setPhotoKeys] = useState<string[]>([]);
  const [totalPhotos, setTotalPhotos] = useState(0);

  // Current photo state
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Zoom/pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // Toast state
  const [showToast, setShowToast] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch photo list for this event
  useEffect(() => {
    if (!awsCredentials || !year || !event) return;

    const s3 = new S3Service(awsCredentials);
    s3.listPhotoKeys(year, event).then((keys) => {
      const sorted = [...keys].sort((a, b) => a.localeCompare(b));
      setPhotoKeys(sorted);
      setTotalPhotos(sorted.length);
    });
  }, [awsCredentials, year, event]);

  // Fetch current photo pre-signed URL
  useEffect(() => {
    if (!awsCredentials || photoKeys.length === 0) return;
    if (photoIndex < 0 || photoIndex >= photoKeys.length) return;

    setIsLoading(true);
    setError(null);
    setScale(1);
    setTranslate({ x: 0, y: 0 });

    const s3 = new S3Service(awsCredentials);
    s3.generatePresignedUrl(photoKeys[photoIndex])
      .then((url) => {
        setPhotoUrl(url);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load photo');
        setIsLoading(false);
      });
  }, [awsCredentials, photoKeys, photoIndex]);

  // Calculate what page this photo is on in the grid
  const calculatePage = useCallback(
    (index: number): number => {
      return Math.floor(index / PHOTOS_PER_PAGE) + 1;
    },
    []
  );

  // Close viewer → navigate back to event grid, preserving page
  const handleClose = useCallback(() => {
    const page = calculatePage(photoIndex);
    navigate(`/years/${year}/${event}?page=${page}`);
  }, [navigate, year, event, photoIndex, calculatePage]);

  // Navigation with debounce to prevent double-swipe
  const isNavigating = useRef(false);

  const goToNext = useCallback(() => {
    if (isNavigating.current) return;
    const nextIdx = getNextIndex(photoIndex, totalPhotos);
    if (nextIdx !== photoIndex) {
      isNavigating.current = true;
      navigate(`/years/${year}/${event}/${nextIdx}`, { replace: true });
      setTimeout(() => { isNavigating.current = false; }, 400);
    }
  }, [navigate, year, event, photoIndex, totalPhotos]);

  const goToPrev = useCallback(() => {
    if (isNavigating.current) return;
    const prevIdx = getPrevIndex(photoIndex, totalPhotos);
    if (prevIdx !== photoIndex) {
      isNavigating.current = true;
      navigate(`/years/${year}/${event}/${prevIdx}`, { replace: true });
      setTimeout(() => { isNavigating.current = false; }, 400);
    }
  }, [navigate, year, event, photoIndex, totalPhotos]);

  // Retry loading on error
  const handleRetry = useCallback(() => {
    if (!awsCredentials || photoKeys.length === 0) return;
    if (photoIndex < 0 || photoIndex >= photoKeys.length) return;

    setIsLoading(true);
    setError(null);

    const s3 = new S3Service(awsCredentials);
    s3.generatePresignedUrl(photoKeys[photoIndex])
      .then((url) => {
        setPhotoUrl(url);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Failed to load photo');
        setIsLoading(false);
      });
  }, [awsCredentials, photoKeys, photoIndex]);

  // Download photo
  const handleDownload = useCallback(async () => {
    if (!awsCredentials || photoKeys.length === 0) return;
    if (photoIndex < 0 || photoIndex >= photoKeys.length) return;

    const key = photoKeys[photoIndex];
    const parts = key.split('/');
    const filename = parts[parts.length - 1];

    try {
      const s3 = new S3Service(awsCredentials);
      const downloadUrl = await s3.generateDownloadUrl(key, filename);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Show toast
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch {
      // Download failed silently for now
    }
  }, [awsCredentials, photoKeys, photoIndex]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'ArrowLeft':
          goToPrev();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, goToNext, goToPrev]);

  // Gesture handling for pinch-zoom, pan, and swipe
  const bind = useGesture(
    {
      onPinch: ({ offset: [s] }) => {
        const clampedScale = Math.min(Math.max(s, 1), 10);
        setScale(clampedScale);

        // Reset translate if zoomed back to 1x
        if (clampedScale <= 1) {
          setTranslate({ x: 0, y: 0 });
        }
      },
      onDrag: ({ offset: [x, y], cancel, swipe: [swipeX] }) => {
        // Swipe navigation when not zoomed
        if (scale <= 1) {
          if (swipeX === 1) {
            goToPrev();
            cancel();
            return;
          }
          if (swipeX === -1) {
            goToNext();
            cancel();
            return;
          }
          // Don't allow panning when not zoomed
          return;
        }

        // Panning within zoomed image bounds
        const container = containerRef.current;
        const image = imageRef.current;
        if (!container || !image) return;

        const containerRect = container.getBoundingClientRect();
        const scaledWidth = image.naturalWidth * scale;
        const scaledHeight = image.naturalHeight * scale;

        // Calculate maximum allowed pan distance
        const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

        const clampedX = Math.min(Math.max(x, -maxX), maxX);
        const clampedY = Math.min(Math.max(y, -maxY), maxY);

        setTranslate({ x: clampedX, y: clampedY });
      },
    },
    {
      pinch: {
        scaleBounds: { min: 1, max: 10 },
        from: () => [scale, 0],
      },
      drag: {
        from: () => [translate.x, translate.y],
        filterTaps: true,
        swipe: { distance: 50, velocity: 0.3 },
        bounds: scale > 1 ? undefined : { left: 0, right: 0, top: 0, bottom: 0 },
      },
    }
  );

  return (
    <div className="viewer-overlay" ref={containerRef}>
      {/* Top controls */}
      <div className="viewer-top-bar">
        <button
          className="viewer-close-btn"
          onClick={handleClose}
          aria-label="Close viewer"
        >
          ✕
        </button>
        <span className="viewer-counter">
          {totalPhotos > 0 ? `${photoIndex + 1} / ${totalPhotos}` : ''}
        </span>
        <button
          className="viewer-download-btn"
          onClick={handleDownload}
          aria-label="Download photo"
        >
          ⬇
        </button>
      </div>

      {/* Main content area */}
      <div className="viewer-content" {...bind()}>
        {isLoading && (
          <div className="viewer-loading">
            <div className="viewer-spinner" aria-label="Loading photo" />
          </div>
        )}

        {error && !isLoading && (
          <div className="viewer-error">
            <p>{error}</p>
            <button onClick={handleRetry} className="viewer-retry-btn">
              Retry
            </button>
          </div>
        )}

        {photoUrl && !isLoading && !error && (
          <img
            ref={imageRef}
            src={photoUrl}
            alt={`Photo ${photoIndex + 1}`}
            className="viewer-image"
            style={{
              transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
              touchAction: 'none',
            }}
            draggable={false}
          />
        )}
      </div>

      {/* Navigation arrows (desktop) */}
      {totalPhotos > 1 && (
        <>
          <button
            className="viewer-nav-btn viewer-nav-prev"
            onClick={goToPrev}
            disabled={photoIndex === 0}
            aria-label="Previous photo"
          >
            ←
          </button>
          <button
            className="viewer-nav-btn viewer-nav-next"
            onClick={goToNext}
            disabled={photoIndex === totalPhotos - 1}
            aria-label="Next photo"
          >
            →
          </button>
        </>
      )}

      {/* Download toast */}
      {showToast && (
        <div className="viewer-toast" role="status" aria-live="polite">
          Photo saved
        </div>
      )}
    </div>
  );
}
